import { useEffect, useMemo, useState } from "react";
import type { FocusEvent } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ArrowPathRoundedSquareIcon, UsersIcon } from "@heroicons/react/24/outline";

import useAccountContacts from "../../data/useAccountContacts";
import useContactLeads from "../../data/useContactLeads";
import useContactSingle from "../../data/useContactsSingle";
import useEmails from "../../data/useEmails";
import useSingleLead from "../../data/useSingleLead";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ContactType, IContact } from "../../models/Contact";
import { SendEmailRequest } from "../../models/Email";
import { ILead } from "../../models/Lead";
import { IAccount } from "../../models/account/IAccount";
import classNames from "../../utils/classNames";
import { SearchAccount } from "../accounts/SearchAccount";
import { shouldRemoveFromAccount } from "../../utils/contactDuplicates";
import Card from "../shared/Card/Card";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { TextInput } from "../shared/Inputs/TextInput";
import LeadsTableCallCount from "../leads/LeadTable/TableSubComponents/LeadsTableCallCount";
import SendQualifierButton from "../leads/SendQualifierButton";
import SendEmail from "./SendEmail";
import SendText from "./SendText";
import { MergedFromBadge } from "./MergeSourceBadges";
import SharedContactLeadsTable from "./SharedContactLeadsTable";
import SharedContactModal from "./SharedContactModal";

type FormValues = {
  fullname: string;
  contactType: ContactType;
  telephone1: string;
  telephone2: string;
  email: string;
  line1: string;
  line2: string;
  town: string;
  county: string;
  postcode: string;
};

function getFormValuesFromLead(currentLead: ILead): FormValues {
  return {
    fullname: currentLead.contact?.fullname?.trim() ?? "",
    contactType: currentLead.contact?.contactType ?? ContactType.Customer,
    telephone1: currentLead.contact?.telephone1 ?? "",
    telephone2: currentLead.contact?.telephone2 ?? "",
    email: currentLead.contact?.email ?? "",
    line1: currentLead.contact?.line1 ? currentLead.contact.line1.trim() : "",
    line2: currentLead.contact?.line2 ?? "",
    town: currentLead.contact?.town ?? "",
    county: currentLead.contact?.county ?? "",
    postcode: currentLead.contact?.postcode ?? "",
  };
}

export default function LeadContactDetails({
  lead,
  variant = "standalone",
}: {
  lead: ILead;
  variant?: "standalone" | "embedded";
}) {
  const { t } = useTranslation("accounts");
  const { lead: singleLead } = useSingleLead(lead.id, "LeadContactDetails");
  const { update } = useContactSingle(lead.contact?.id!);
  const { contactLeads, split } = useContactLeads(lead.contact?.id, "LeadContactDetails");
  const { sendEmail } = useEmails();
  const [emailOpen, setEmailOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<IAccount | null>(lead.contact?.account ?? null);
  // Set only when the user clears the account themselves, so a lead whose account simply did not
  // load never unlinks the contact on save.
  const [accountCleared, setAccountCleared] = useState(false);
  const { accountContacts } = useAccountContacts(selectedAccount?.id, "LeadContactDetails");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Contact details"));
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isDirty, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: getFormValuesFromLead(lead),
  });

  const contactTypes = [...Object.values(ContactType).filter((ct) => isNaN(Number(ct)))] as string[];

  // Widget leads arrive on a contact the widget has already deduplicated on email + phone, so one
  // seller quoting several vehicles lands every lead on the same contact row. Editing it here
  // would silently rewrite the details on all of them, hence the indicator and the warning below.
  const otherLeads = useMemo(
    () => (contactLeads.data?.data ?? []).filter((l) => l.id !== lead.id),
    [contactLeads.data?.data, lead.id]
  );
  const isSharedContact = otherLeads.length > 0;
  // Set when a contact merge brought this lead onto the contact.
  const mergedFrom = (contactLeads.data?.data ?? []).find((l) => l.id === lead.id)?.mergedFrom;
  const [sharedOpen, setSharedOpen] = useState(false);
  const [sharedLeadsOpen, setSharedLeadsOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);

  const buildContactPayload = (data: FormValues) => ({
    ...lead.contact,
    ...data,
    contactType: parseInt(String(data.contactType), 10),
    accountId: selectedAccount?.id,
    // A missing accountId keeps the link; only "Change account" with nothing picked takes it off.
    removeFromAccount: shouldRemoveFromAccount(accountCleared, selectedAccount?.id),
    leadId: lead.id,
  });

  const resetForm = (data: FormValues) =>
    reset({
      fullname: data.fullname,
      contactType: parseInt(String(data.contactType), 10),
      telephone1: data.telephone1,
      telephone2: data.telephone2,
      email: data.email,
      line1: data.line1,
      line2: data.line2,
      town: data.town,
      county: data.county,
      postcode: data.postcode,
    });

  // Keeps the shared contact and rewrites it for every lead on it.
  const saveToSharedContact = (data: FormValues) => {
    update.mutate(buildContactPayload(data) as IContact, {
      onError: saveHandlers.onError,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setSharedOpen(false);
        setPendingValues(null);
      },
    });

    resetForm(data);
  };

  // Gives this lead its own copy of the contact, carrying the edits across, so the other leads
  // keep the details they have now.
  const saveAsNewContact = (data: FormValues | null) => {
    split.mutate(
      {
        leadId: lead.id,
        contact: data ? (buildContactPayload(data) as Partial<IContact>) : null,
      },
      {
        onError: saveHandlers.onError,
        onSuccess: () => {
          saveHandlers.onSuccess();
          setSharedOpen(false);
          setPendingValues(null);
        },
      }
    );

    if (data) resetForm(data);
  };

  const onSubmit = (data: FormValues) => {
    // Ask first when the edit would reach beyond this lead.
    if (isSharedContact) {
      setPendingValues(data);
      setSharedOpen(true);
      return;
    }

    saveToSharedContact(data);
  };

  const handleRequestDocs = () => {
    console.log("Requesting docs");
    setIsRequestLoading(true);

    const email: SendEmailRequest = {
      contactId: lead.contact?.id!,
      leadId: lead.id,
      recipient: lead.contact?.email!,
      subject: "Please upload your documents",
      body: null,
      templateId: "3748E3DE-1AE9-4051-84F4-08DB2AD19047",
      substitutions: {
        "{{name}}": lead.contact?.fullname!,
        "{{vrm}}": lead.vehicle?.vrm!,
      },
    };

    sendEmail.mutate(email, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setRequestOpen(false);
      },
      onError: (error: unknown) => {
        console.log("Error: ", error);
        saveHandlers.onError();
      },
      onSettled: () => {
        setIsRequestLoading(false);
      },
    });
  };

  const primaryPhone = watch("telephone1");
  const additionalPhone = watch("telephone2");
  const [invalidPhone, setInvalidPhone] = useState(false);

  useEffect(() => {
    if (primaryPhone === "") {
      setInvalidPhone(true);
      return;
    }
    const isValid1 = /^\d{11}$/.test(primaryPhone);
    const isValidWithSpace = /^\d{5} \d{6}$/.test(primaryPhone);
    const isValidWithSpace2 = /^\d{5} \d{3} \d{3}$/.test(primaryPhone);
    const isValidWithAreaCode = /^\+44\d{10}$/.test(primaryPhone);
    const isValidWithAreaCode2 = /^\+44 \d{4} \d{3} \d{3}$/.test(primaryPhone);
    const isValidWithAreaCode3 = /^\+44 \d{4} \d{6}$/.test(primaryPhone);

    const isValid =
      isValid1 ||
      isValidWithSpace ||
      isValidWithSpace2 ||
      isValidWithAreaCode ||
      isValidWithAreaCode2 ||
      isValidWithAreaCode3;

    if (!isValid) {
      setInvalidPhone(true);
      return;
    }

    setInvalidPhone(false);
  }, [primaryPhone]);

  const phoneSwap = () => {
    setValue("telephone1", additionalPhone);
    setValue("telephone2", primaryPhone);
  };

  useEffect(() => {
    reset(getFormValuesFromLead(lead));
  }, [lead.id, lead.contact?.id, lead.contact?.updated, reset]);

  useEffect(() => {
    setAccountCleared(false);
    if (lead.contact?.account) {
      setSelectedAccount(lead.contact.account);
      return;
    }
    setSelectedAccount(null);
  }, [lead.id, lead.contact?.id, lead.contact?.account?.id]);

  const handleAccountSelect = (account: IAccount) => {
    setSelectedAccount(account);
    setAccountCleared(false);
  };

  const handleChangeAccount = () => {
    setSelectedAccount(null);
    setAccountCleared(true);
  };

  const accountContactToApply = useMemo(() => {
    const contacts = accountContacts.data?.data ?? [];
    if (contacts.length === 0) return null;

    if (selectedAccount?.primaryContactId) {
      return contacts.find((c) => c.id === selectedAccount.primaryContactId) ?? contacts[0];
    }

    return contacts[0];
  }, [accountContacts.data?.data, selectedAccount?.primaryContactId]);

  const handleUpdateFromAccount = () => {
    if (!accountContactToApply) return;

    const updatedValues: FormValues = {
      ...getValues(),
      fullname: accountContactToApply.fullname ?? "",
      email: accountContactToApply.email ?? "",
      telephone1: accountContactToApply.telephone1 ?? "",
      telephone2: accountContactToApply.telephone2 ?? "",
      line1: accountContactToApply.line1 ?? "",
      line2: accountContactToApply.line2 ?? "",
      town: accountContactToApply.town ?? "",
      county: accountContactToApply.county ?? "",
      postcode: accountContactToApply.postcode ?? "",
    };

    reset(updatedValues);
    onSubmit(updatedValues);
  };

  const saveOnBlur = (event: FocusEvent<HTMLFormElement>) => {
    const target = event.target as HTMLElement | null;
    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLSelectElement) &&
      !(target instanceof HTMLTextAreaElement)
    ) {
      return;
    }

    const nextTarget = event.relatedTarget as HTMLElement | null;
    if (nextTarget?.getAttribute("type") === "submit") {
      return;
    }

    if (!isDirty || isSubmitting || update.isLoading || invalidPhone || sharedOpen) {
      return;
    }

    handleSubmit(onSubmit)();
  };

  const inner = (
    <>
      {variant === "embedded" && (
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t("Contact")}</h3>
      )}

      {mergedFrom && (
        <div>
          <MergedFromBadge mergedFrom={mergedFrom} />
        </div>
      )}

      {isSharedContact && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3">
          <div className="flex flex-wrap items-start gap-2">
            <UsersIcon className="mt-0.5 h-5 w-5 shrink-0 text-yellow-600" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-yellow-800">
                {otherLeads.length === 1
                  ? t("Shared contact - also used by {{count}} other lead", { count: otherLeads.length })
                  : t("Shared contact - also used by {{count}} other leads", { count: otherLeads.length })}
              </p>
              <p className="mt-0.5 text-xs text-yellow-700">
                {t("Changing these details changes them on every lead below. Split the contact to give this lead its own copy.")}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <ButtonInput
                  label={
                    sharedLeadsOpen
                      ? t("Hide leads")
                      : otherLeads.length === 1
                        ? t("Show {{count}} lead", { count: otherLeads.length })
                        : t("Show {{count}} leads", { count: otherLeads.length })
                  }
                  isSubmit={false}
                  onClick={() => setSharedLeadsOpen((open) => !open)}
                  size="sm"
                  classes="border border-yellow-300 !bg-white !text-yellow-800 hover:!bg-yellow-100"
                />
                <ButtonInput
                  label={t("Split contact")}
                  isSubmit={false}
                  onClick={() => saveAsNewContact(isDirty ? getValues() : null)}
                  isLoading={split.isLoading}
                  disabled={split.isLoading}
                  size="sm"
                  classes=""
                />
              </div>
            </div>
          </div>
          {sharedLeadsOpen && (
            <div className="mt-3">
              <SharedContactLeadsTable leads={otherLeads} />
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} onBlurCapture={saveOnBlur} className="space-y-2">
        <div className="border-b border-gray-200 pb-2">
          <div className="flex flex-wrap items-end gap-3">
            <div
              className={classNames(
                "min-w-0 flex-1",
                selectedAccount && "pointer-events-none opacity-60"
              )}
            >
              <SearchAccount
                label={t("Search customer account")}
                selectedAccount={selectedAccount}
                onAccountSelect={handleAccountSelect}
                disabled={!lead.contact?.id || !!selectedAccount}
              />
            </div>
            {selectedAccount && (
              <ButtonInput
                label={t("Change account")}
                isSubmit={false}
                onClick={handleChangeAccount}
                classes="shrink-0"
              />
            )}
          </div>
          {selectedAccount && (
            <p className="mt-1 text-xs text-gray-700">
              {t("Current account: {{name}} (#{{number}})", {
                name: selectedAccount.name,
                number: selectedAccount.accountNumber,
              })}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {t("Link this contact to an existing account to prevent duplicate customer creation.")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-1">
          <TextInput label={t("Name")} register={register} registerName={"fullname"} rounded />
          <TextInput label={t("Email Address")} register={register} registerName={"email"} rounded />
          <div className="relative flex gap-2">
            <div className="min-w-0 flex-1">
              <TextInput label={t("Phone Number")} register={register} registerName={"telephone1"} rounded />
            </div>
            <button
              type="button"
              className="mt-7 shrink-0 self-start rounded-md p-1 text-gray-400 transition-all hover:scale-110 hover:text-primary-600"
              onClick={phoneSwap}
              aria-label={t("Swap phone numbers")}
            >
              <ArrowPathRoundedSquareIcon className="h-6 w-6" />
            </button>
          </div>
          {invalidPhone && <div className="text-sm text-red-500">{t("Please enter a valid mobile phone number")}</div>}
          <TextInput label={t("Additional Number")} register={register} registerName={"telephone2"} rounded />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end">
          <div>
            <label className="text-sm font-medium text-gray-500">{t("Contact Type")}</label>
            <select
              {...register("contactType")}
              className="mt-1 w-full rounded-md border-gray-300 text-sm text-gray-900"
            >
              {contactTypes.map((ct: string, i: number) => (
                <option key={ct} value={i}>
                  {t(ct)}
                </option>
              ))}
            </select>
          </div>
          <LeadsTableCallCount lead={lead} />
        </div>

        <div className="flex flex-col items-stretch justify-between gap-3 py-2 sm:flex-row sm:items-center">
          <ButtonInput label={t("Update")} isSubmit onClick={() => {}} classes="" />
          <ButtonInput
            label={t("Update From Account")}
            isSubmit={false}
            onClick={handleUpdateFromAccount}
            disabled={!selectedAccount || !accountContactToApply}
            classes="disabled:!bg-gray-400"
          />
        </div>
      </form>

      <hr className="border-gray-200" />

      <div className="flex flex-col gap-4 pt-2 lg:flex-row lg:flex-wrap lg:justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            className="rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
            onClick={() => setEmailOpen(true)}
            type="button"
          >
            {t("Send Email")}
          </button>
          <button
            className="rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
            onClick={() => setTextOpen(true)}
            type="button"
          >
            {t("Send SMS")}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <SendQualifierButton lead={lead} />
          <button
            className={classNames(
              singleLead?.data?.data?.documentsRequested
                ? "bg-green-600 hover:bg-green-700"
                : "bg-primary-600 hover:bg-primary-700",
              "rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
            )}
            onClick={() => setRequestOpen(true)}
            type="button"
          >
            {t("Request Customer Docs")}
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <ConfirmModal
        open={requestOpen}
        setOpen={setRequestOpen}
        onConfirm={handleRequestDocs}
        isLoading={isRequestLoading}
        title={t("Request documents from customer?")}
        message={t("This will send an email to the customer requesting they upload their documents and update their payment information.")}
        confirmButtonText={t("Request documents")}
      />
      <SharedContactModal
        open={sharedOpen}
        setOpen={(open: boolean) => {
          setSharedOpen(open);
          if (!open) setPendingValues(null);
        }}
        otherLeads={otherLeads}
        onChangeAll={() => pendingValues && saveToSharedContact(pendingValues)}
        onSaveAsNew={() => pendingValues && saveAsNewContact(pendingValues)}
        isLoading={update.isLoading || split.isLoading}
      />
      <Modal open={emailOpen} setOpen={setEmailOpen} width="max-w-7xl max-h-[75vh]">
        <label>{t("Send an email to the customer")}</label>
        <SendEmail lead={lead} setOpen={setEmailOpen} />
      </Modal>
      <Modal open={textOpen} setOpen={setTextOpen} width="max-w-7xl">
        <label>{t("Send an SMS to the customer")}</label>
        <SendText lead={lead} setOpen={setTextOpen} />
      </Modal>

      {variant === "embedded" ? (
        <div className="min-w-0 space-y-2 xl:border-r xl:border-gray-200 xl:pr-8">{inner}</div>
      ) : (
        <Card title={t("Contact Details")} bodyClassName="p-4" collapsible>
          <div className="space-y-2">{inner}</div>
        </Card>
      )}
    </>
  );
}
